<template>
  <div class="comet-i18n-exclude">
    <custom-field-label :field="{ label: fieldLabel, required: true }" />

    <div
      :class="`base-smartgate-autocomplete-select-container autocomplete-select-${cssFieldClassName}`">
      <v-autocomplete
        required
        variant="outlined"
        v-model="selectedItem"
        @update:search="onSearchInput"
        @blur="onBlur"
        @update:model-value="$emit('update:model-value', selectedItem)"
        v-model:search="searchValue"
        :data-testid="testId"
        :items="items"
        :placeholder="placeholder"
        item-value="id"
        item-title="name"
        return-object
        :maxlength="Number(fieldMaxLength)"
        clearable
        no-filter
        :menu-props="{ attach: `.autocomplete-select-${cssFieldClassName}` }"
        ref="autoCompleteSelect"
        persistent-hint
        :loading="isLoading"
        :rules="rules">
        <template
          v-slot:prepend-item
          v-if="!isLoading && searchValue?.length > 0 && items.length > 0">
          <v-list-item
            :data-testid="customItemTestId"
            :class="customItemClass"
            role="option"
            @click="addCustomItem">
            <v-list-item-title>
              {{ t('smartGate.selectAsField', { value: searchValue, fieldName }) }}
            </v-list-item-title>
          </v-list-item>
          <v-divider v-if="!isLoading && searchValue" />
        </template>

        <template v-slot:item="{ item, props }">
          <v-list-item v-bind="props" :title="null" role="option">
            <v-list-item-title v-html="`${item.raw.name}`"></v-list-item-title>
          </v-list-item>

          <v-divider
            v-if="items.indexOf(item.raw) < items.length - 1"
            :key="items.indexOf(item.raw)" />
        </template>

        <template #no-data>
          <v-list-item
            :data-testid="customItemTestId"
            role="option"
            :class="customItemClass"
            v-if="!isLoading && searchValue?.length >= 1 && items.length === 0"
            @click="addCustomItem">
            <v-list-item-title>
              {{ t('smartGate.selectAsField', { value: searchValue, fieldName }) }}
            </v-list-item-title>
          </v-list-item>
          <v-list-item v-else>{{ t('smartGate.typeToSearchField', { fieldName }) }}</v-list-item>
        </template>
      </v-autocomplete>
    </div>
  </div>
</template>

<script>
import { computed, defineComponent, onMounted, ref, watch } from 'vue';
import { isFunction } from 'lodash';
import { useI18n } from 'vue-i18n';

import { useAxiosClient } from './composables';

export default defineComponent({
  props: {
    warehouseId: {
      type: String,
      required: true
    },
    fieldLabel: {
      type: String,
      required: true
    },
    fieldName: {
      type: String,
      required: true
    },
    fieldMaxLength: {
      type: Number,
      required: false,
      default: 8
    },
    placeholder: {
      type: String,
      required: true
    },
    testId: {
      type: String,
      required: true
    },
    customItemTestId: {
      type: String,
      required: true
    },
    customItemClass: {
      type: String,
      required: true
    },
    dataMapper: {
      type: Function,
      required: true
    },
    rules: {
      type: Array,
      required: false,
      default: () => []
    },
    /** Restores selection when the component remounts (e.g. returning from ID verification). */
    modelValue: {
      type: Object,
      default: null
    }
  },
  emits: ['update:model-value'],
  setup(props, { emit }) {
    const { t } = useI18n();
    const cssFieldClassName = computed(() => props.customItemClass);

    const items = ref([]);
    const searchValue = ref(null);
    const selectedItem = ref(null);
    const isLoading = ref(false);
    const autoCompleteSelect = ref(null);
    const axiosClient = useAxiosClient();

    watch(
      () => props.modelValue,
      value => {
        if (value && value.name) {
          selectedItem.value = {
            id: value.id ?? null,
            name: value.name
          };
        } else {
          selectedItem.value = null;
        }
      },
      { immediate: true }
    );

    const getData = async () => {
      isLoading.value = true;

      try {
        const response = await axiosClient.get(
          `/observations/public/${props.warehouseId}/autocomplete`
        );

        const responseItems = response.data || [];
        items.value = responseItems.map(props.dataMapper).filter(item => item !== undefined);
      } catch (err) {
        items.value = [];
        console.error(`Error fetching ${props.fieldLabel}s:`, err);
      } finally {
        isLoading.value = false;
      }
    };

    const onSearchInput = async inputVal => {
      searchValue.value = inputVal;
    };

    const addCustomItem = () => {
      if (searchValue.value?.trim()) {
        selectedItem.value = {
          id: null,
          name: searchValue.value
        };

        // Close the dropdown after selecting the custom item
        if (isFunction(autoCompleteSelect.value.blur)) {
          autoCompleteSelect.value.blur();
        }

        emit('update:model-value', selectedItem.value);
      }
    };

    const onBlur = () => {
      const typed = searchValue.value?.trim();
      if (typed && typed !== selectedItem.value?.name) {
        addCustomItem();
      }
    };

    onMounted(async () => {
      await getData();
    });

    return {
      t,
      cssFieldClassName,
      items,
      searchValue,
      selectedItem,
      isLoading,
      autoCompleteSelect,
      addCustomItem,
      onBlur,
      getData,
      onSearchInput
    };
  }
});
</script>
<style lang="scss">
.add-custom-license-plate,
.add-custom-dot-number {
  min-height: 60px !important;
  position: sticky;
  top: -8px;
  z-index: 2;
  background: $white;
}
.base-smartgate-autocomplete-select-container {
  position: relative;
}
.base-smartgate-autocomplete-select-container
  .v-menu
  > .v-overlay__content.v-autocomplete__content {
  top: 57px !important;
}
</style>
